'use client';

import * as React from 'react';

import { cn } from '../lib/cn';

import { Label } from './label';

interface FieldContextValue {
  id: string;
  descriptionId: string;
  errorId: string;
  hasError: boolean;
}

const FieldContext = React.createContext<FieldContextValue | null>(null);

function useFieldContext(): FieldContextValue {
  const ctx = React.useContext(FieldContext);
  if (!ctx) {
    throw new Error('Field subcomponents must be used inside <Field>');
  }
  return ctx;
}

export interface FieldProps extends React.HTMLAttributes<HTMLDivElement> {
  id?: string;
  error?: string;
}

export function Field({
  className,
  id,
  error,
  children,
  ...props
}: FieldProps): React.ReactElement {
  const generatedId = React.useId();
  const fieldId = id ?? `field-${generatedId}`;
  const value = React.useMemo<FieldContextValue>(
    () => ({
      id: fieldId,
      descriptionId: `${fieldId}-description`,
      errorId: `${fieldId}-error`,
      hasError: Boolean(error),
    }),
    [fieldId, error],
  );

  return (
    <FieldContext.Provider value={value}>
      <div className={cn('flex flex-col gap-1.5', className)} {...props}>
        {children}
        {error ? <FieldError>{error}</FieldError> : null}
      </div>
    </FieldContext.Provider>
  );
}

export const FieldLabel = React.forwardRef<
  React.ComponentRef<typeof Label>,
  React.ComponentPropsWithoutRef<typeof Label>
>(({ className, ...props }, ref) => {
  const { id } = useFieldContext();
  return <Label ref={ref} htmlFor={id} className={className} {...props} />;
});
FieldLabel.displayName = 'FieldLabel';

export interface FieldControlProps {
  children: React.ReactElement;
}

export function FieldControl({ children }: FieldControlProps): React.ReactElement {
  const { id, descriptionId, errorId, hasError } = useFieldContext();
  const child = React.Children.only(children) as React.ReactElement<
    Record<string, unknown> & { 'aria-describedby'?: string }
  >;

  const existingDescribedBy = child.props['aria-describedby'];
  const describedByParts = [existingDescribedBy, descriptionId, hasError ? errorId : undefined]
    .filter(Boolean)
    .join(' ');

  return React.cloneElement(child, {
    id,
    'aria-describedby': describedByParts || undefined,
    'aria-invalid': hasError ? true : child.props['aria-invalid'],
  });
}

export function FieldDescription({
  className,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement>): React.ReactElement {
  const { descriptionId } = useFieldContext();
  return (
    <p id={descriptionId} className={cn('text-muted-foreground text-xs', className)} {...props} />
  );
}

export function FieldError({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement>): React.ReactElement | null {
  const { errorId } = useFieldContext();
  if (!children) return null;
  return (
    <p
      id={errorId}
      role="alert"
      className={cn('text-destructive text-xs font-medium', className)}
      {...props}
    >
      {children}
    </p>
  );
}
